import type { paths } from './schema.js';

/**
 * Typed API client.
 *
 * Paths, methods, request bodies and response shapes all come from `schema.ts`, which is
 * generated from the OpenAPI document the server produces from the schemas it enforces. A
 * contract change therefore breaks the build here rather than at runtime in front of a user.
 *
 * The client owns exactly the transport concerns a generated client does not: credentials,
 * correlation headers, the workspace header, and turning a problem document into an error.
 */

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  code: string;
  requestId: string;
  errors?: { pointer: string; code: string; message: string }[];
  retryAfterSeconds?: number;
}

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }

  /** Stable code the UI switches on, and which the i18n catalogue has a message for. */
  get code(): string {
    return this.problem.code;
  }

  get status(): number {
    return this.problem.status;
  }

  fieldError(pointer: string): string | undefined {
    return this.problem.errors?.find((entry) => entry.pointer === pointer)?.message;
  }
}

/** A network failure or an unparseable response — not something the server told us. */
export class TransportError extends Error {
  constructor(cause: unknown) {
    super('The service could not be reached');
    this.name = 'TransportError';
    this.cause = cause;
  }
}

type Paths = paths;
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const WEB_CLIENT_PROOF = 'mailbutler-web';

type PathOf<M extends string> = {
  [P in keyof Paths]: M extends keyof Paths[P] ? P : never;
}[keyof Paths];

type Operation<P extends keyof Paths, M extends string> = M extends keyof Paths[P]
  ? Paths[P][M]
  : never;

type BodyOf<O> = O extends { requestBody?: { content: { 'application/json': infer B } } }
  ? B
  : O extends { requestBody: { content: { 'application/json': infer B } } }
    ? B
    : undefined;

type ResponseOf<O> = O extends { responses: infer R }
  ? R extends Record<number, { content: { 'application/json': infer T } }>
    ? R extends { 200: { content: { 'application/json': infer T200 } } }
      ? T200
      : R extends { 201: { content: { 'application/json': infer T201 } } }
        ? T201
        : R extends { 202: { content: { 'application/json': infer T202 } } }
          ? T202
          : T
    : void
  : void;

export interface RequestOptions {
  /** Names the workspace for tenant-scoped routes. */
  readonly tenantId?: string | undefined;
  /** Replay protection for operations that must not happen twice. */
  readonly idempotencyKey?: string | undefined;
  readonly signal?: AbortSignal | undefined;
}

function buildUrl(path: string, params: Record<string, string | number> | undefined): string {
  let resolved = path;
  const query = new URLSearchParams();

  for (const [key, value] of Object.entries(params ?? {})) {
    const placeholder = `{${key}}`;
    if (resolved.includes(placeholder)) {
      resolved = resolved.replace(placeholder, encodeURIComponent(String(value)));
    } else {
      query.set(key, String(value));
    }
  }

  const search = query.toString();
  return search === '' ? resolved : `${resolved}?${search}`;
}

async function request<T>(
  method: string,
  path: string,
  init: {
    body?: unknown;
    params?: Record<string, string | number> | undefined;
    options?: RequestOptions | undefined;
  } = {},
): Promise<T> {
  const { body, params, options } = init;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, params), {
      method,
      // Sessions are cookie-based, so the browser never holds a token that injected script
      // could read.
      credentials: 'same-origin',
      ...(options?.signal === undefined ? {} : { signal: options.signal }),
      headers: {
        accept: 'application/json',
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        // A few privacy tools and proxy configurations remove Origin from same-origin requests.
        // This custom header is not sent by HTML forms and forces a CORS preflight cross-origin,
        // so it is a safe fallback for the cookie CSRF check rather than a bearer secret.
        ...(SAFE_METHODS.has(method) ? {} : { 'x-mailbutler-client': WEB_CLIENT_PROOF }),
        ...(options?.tenantId === undefined ? {} : { 'x-tenant-id': options.tenantId }),
        ...(options?.idempotencyKey === undefined
          ? {}
          : { 'idempotency-key': options.idempotencyKey }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    throw new TransportError(error);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  let payload: unknown;
  try {
    payload = response.status === 204 ? undefined : await response.json();
  } catch (error) {
    if (response.ok) throw new TransportError(error);
    payload = undefined;
  }

  if (!response.ok) {
    if (isProblem(payload)) throw new ApiError(payload);
    throw new ApiError({
      type: 'about:blank',
      title: 'Request failed',
      status: response.status,
      code: 'INTERNAL_ERROR',
      requestId: response.headers.get('x-request-id') ?? 'unknown',
    });
  }

  return payload as T;
}

function isProblem(value: unknown): value is Problem {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'status' in value &&
    'requestId' in value
  );
}

export const api = {
  get<P extends PathOf<'get'>>(
    path: P,
    params?: Record<string, string | number>,
    options?: RequestOptions,
  ): Promise<ResponseOf<Operation<P, 'get'>>> {
    return request('GET', path, { params, options });
  },

  post<P extends PathOf<'post'>>(
    path: P,
    body?: BodyOf<Operation<P, 'post'>>,
    // Path placeholders and query values, in the same position as `patch` and `delete`, so a
    // caller never has to remember which verb puts them where.
    params?: Record<string, string | number>,
    options?: RequestOptions,
  ): Promise<ResponseOf<Operation<P, 'post'>>> {
    return request('POST', path, { body, params, options });
  },

  patch<P extends PathOf<'patch'>>(
    path: P,
    body: BodyOf<Operation<P, 'patch'>>,
    params?: Record<string, string | number>,
    options?: RequestOptions,
  ): Promise<ResponseOf<Operation<P, 'patch'>>> {
    return request('PATCH', path, { body, params, options });
  },

  delete<P extends PathOf<'delete'>>(
    path: P,
    params?: Record<string, string | number>,
    body?: BodyOf<Operation<P, 'delete'>>,
    options?: RequestOptions,
  ): Promise<ResponseOf<Operation<P, 'delete'>>> {
    return request('DELETE', path, { body, params, options });
  },
};
